import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Card, Col, DatePicker, Row, Select, Space, Statistic, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useCurrency } from '@/contexts/CurrencyContext';
import { listSalesTeams } from '@/services/salesTeam.service';
import salesTargetService, {
  WEEKLY_PERFORMANCE_METRICS,
  type SalesPerformanceMetrics,
  type WeeklyMetricGroup,
  type WeeklyPerformanceRow,
  type WeeklySalesPerformance,
} from '@/services/salesTarget.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatNumber } from '@/utils/format/number';
import { formatPercent } from '@/utils/format/percent';
import { formatDate } from '@/utils/format/date';

dayjs.extend(isoWeek);

const { Title, Paragraph, Text } = Typography;

type Metric = (typeof WEEKLY_PERFORMANCE_METRICS)[number];

/** Overdue follow-ups and lost value are better when they go down. */
const LOWER_IS_BETTER = new Set<keyof SalesPerformanceMetrics>(['overdue_follow_ups', 'lost_value']);

function Delta({ metric, now, before }: { metric: Metric; now: number; before: number }) {
  const d = now - before;
  if (!d) return null;
  const good = LOWER_IS_BETTER.has(metric.key) ? d < 0 : d > 0;
  return (
    <Text type={good ? 'success' : 'danger'} style={{ fontSize: 12 }}>
      {d > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {formatNumber(Math.abs(d), { maximumFractionDigits: 0 })}
    </Text>
  );
}

const WeeklySalesPerformancePage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { formatCurrency } = useCurrency();
  const week = dayjs(params.get('week_start') || undefined).isoWeekday(1);
  const weekStart = week.format('YYYY-MM-DD');
  const teamId = params.get('sales_team_id') || undefined;

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const { data: teams = [] } = useQuery({ queryKey: ['sales-teams'], queryFn: () => listSalesTeams(true) });
  const { data, isLoading, error } = useQuery<WeeklySalesPerformance>({
    queryKey: ['weekly-sales-performance', weekStart, teamId],
    queryFn: () => salesTargetService.weeklyPerformance({ week_start: weekStart, sales_team_id: teamId }),
  });

  const fmt = (m: Metric, n: number) => (m.money ? formatCurrency(n) : formatNumber(n, { maximumFractionDigits: 0 }));

  const metricColumns = (group: WeeklyMetricGroup) =>
    WEEKLY_PERFORMANCE_METRICS.filter((m) => m.group === group).map((m) => ({
      title: m.label,
      key: m.key,
      width: m.money ? 170 : 120,
      align: 'right' as const,
      render: (_: unknown, r: WeeklyPerformanceRow) => {
        const actual = r.actual[m.key];
        const target = m.target ? r.target?.[m.target] : null;
        const pct = target != null && target > 0 ? (actual / target) * 100 : null;
        return (
          <Tooltip title={`Last week: ${fmt(m, r.previous[m.key])}`}>
            <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
              <Text>{fmt(m, actual)}</Text>
              {target != null && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  of {fmt(m, target)}
                  {pct != null && (
                    <Tag color={pct >= 100 ? 'green' : pct >= 70 ? 'gold' : 'red'} style={{ marginLeft: 4, marginRight: 0 }}>
                      {formatPercent(pct, { maximumFractionDigits: 0 })}
                    </Tag>
                  )}
                </Text>
              )}
              <Delta metric={m} now={actual} before={r.previous[m.key]} />
            </Space>
          </Tooltip>
        );
      },
    }));

  const performanceColumns = (nameLabel: string): ColumnsType<WeeklyPerformanceRow> => [
    { title: nameLabel, dataIndex: 'name', key: 'name', fixed: 'left', width: 180, render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Activity', key: 'g-activity', children: metricColumns('activity') },
    { title: 'Pipeline', key: 'g-pipeline', children: metricColumns('pipeline') },
  ];

  const teamTargetTotal = useMemo(() => {
    const out: Partial<Record<keyof SalesPerformanceMetrics, number>> = {};
    for (const m of WEEKLY_PERFORMANCE_METRICS) {
      if (!m.target) continue;
      let sum = 0;
      let any = false;
      for (const r of data?.teams ?? []) {
        const v = r.target?.[m.target];
        if (v != null) {
          sum += v;
          any = true;
        }
      }
      if (any) out[m.key] = sum;
    }
    return out;
  }, [data]);

  const summaryCards = WEEKLY_PERFORMANCE_METRICS.filter((m) =>
    ['activities', 'customer_meetings', 'new_opportunities', 'quotations_value', 'won_value', 'overdue_follow_ups'].includes(m.key)
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Weekly sales performance
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Activities, meetings, pipeline, quotations and wins per salesperson and team, against the prorated{' '}
            <Link to="/growth/sales-targets">sales targets</Link> and last week.
          </Paragraph>
        </div>
        <ReportDocumentActions
          reportKey="weekly-sales-performance"
          params={{ week_start: weekStart, sales_team_id: teamId ?? '' }}
          sendPermissions={['growth.reports.view', 'growth.sales_target.manage']}
        />
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <DatePicker
            picker="week"
            value={week}
            allowClear={false}
            onChange={(d) => d && update({ week_start: d.isoWeekday(1).format('YYYY-MM-DD') })}
          />
          <Text type="secondary">
            {formatDate(weekStart)} – {formatDate(week.add(6, 'day').format('YYYY-MM-DD'))}
          </Text>
          <Select
            allowClear
            placeholder="All sales teams"
            style={{ minWidth: 220 }}
            value={teamId}
            onChange={(v) => update({ sales_team_id: v })}
            options={teams.map((t) => ({ value: t.id, label: t.team_name }))}
          />
        </Space>
      </Card>

      {error && <Alert type="error" showIcon message="Could not load the weekly report" style={{ marginBottom: 16 }} />}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {summaryCards.map((m) => {
          const now = data?.total.actual[m.key] ?? 0;
          const before = data?.total.previous[m.key] ?? 0;
          const target = teamTargetTotal[m.key];
          return (
            <Col xs={12} md={8} xl={4} key={m.key}>
              <Card size="small" loading={isLoading}>
                <Statistic title={m.label} value={fmt(m, now)} />
                <Space size={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Last week {fmt(m, before)}
                  </Text>
                  <Delta metric={m} now={now} before={before} />
                </Space>
                {target != null && target > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Target {fmt(m, target)} · {formatPercent((now / target) * 100, { maximumFractionDigits: 0 })}
                    </Text>
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card size="small">
        <Tabs
          items={[
            {
              key: 'people',
              label: 'By salesperson',
              children: (
                <Table<WeeklyPerformanceRow>
                  rowKey="id"
                  size="small"
                  loading={isLoading}
                  columns={performanceColumns('Salesperson')}
                  dataSource={data?.people ?? []}
                  pagination={false}
                  scroll={{ x: 2000 }}
                  locale={{ emptyText: 'No sales activity this week' }}
                />
              ),
            },
            ...(!teamId
              ? [
                  {
                    key: 'teams',
                    label: 'By team',
                    children: (
                      <Table<WeeklyPerformanceRow>
                        rowKey="id"
                        size="small"
                        loading={isLoading}
                        columns={performanceColumns('Team')}
                        dataSource={data?.teams ?? []}
                        pagination={false}
                        scroll={{ x: 2000 }}
                        locale={{ emptyText: 'No team activity this week' }}
                      />
                    ),
                  },
                ]
              : []),
            {
              key: 'types',
              label: 'By activity type',
              children: (
                <Table
                  rowKey="type_name"
                  size="small"
                  loading={isLoading}
                  dataSource={data?.activities_by_type ?? []}
                  pagination={false}
                  locale={{ emptyText: 'No activities logged' }}
                  columns={[
                    { title: 'Type', dataIndex: 'type_name' },
                    { title: 'This week', dataIndex: 'this_week', align: 'right' },
                    { title: 'Last week', dataIndex: 'last_week', align: 'right' },
                    {
                      title: 'Change',
                      key: 'change',
                      align: 'right',
                      render: (_, r) => (
                        <Delta metric={WEEKLY_PERFORMANCE_METRICS[0]} now={r.this_week} before={r.last_week} />
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              key: 'lost',
              label: `Lost (${data?.lost_reasons.length ?? 0})`,
              children: (
                <>
                  {!!data?.lost_reason_summary.length && (
                    <Space wrap style={{ marginBottom: 12 }}>
                      {data.lost_reason_summary.map((s) => (
                        <Tag key={s.reason}>
                          {s.reason}: {s.count} · {formatCurrency(s.value)}
                        </Tag>
                      ))}
                    </Space>
                  )}
                  <Table
                    rowKey="opportunity_id"
                    size="small"
                    loading={isLoading}
                    dataSource={data?.lost_reasons ?? []}
                    pagination={false}
                    locale={{ emptyText: 'No opportunities lost this week' }}
                    columns={[
                      {
                        title: 'Opportunity',
                        key: 'opp',
                        render: (_, r) => (
                          <Link to={`/growth/opportunities/${r.opportunity_id}`}>
                            {r.opportunity_number} – {r.opportunity_name}
                          </Link>
                        ),
                      },
                      { title: 'Customer', dataIndex: 'customer_name' },
                      { title: 'Owner', dataIndex: 'owner_name' },
                      { title: 'Reason', dataIndex: 'reason', render: (v: string | null) => v || <Text type="secondary">Not recorded</Text> },
                      { title: 'Value', dataIndex: 'value', align: 'right', render: (v: number) => formatCurrency(v) },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'stale',
              label: `Stale deals (${data?.stale_deals.length ?? 0})`,
              children: (
                <>
                  <Paragraph type="secondary">
                    Open opportunities with no sales activity for {data?.stale_deal_days ?? 30} days or more.
                  </Paragraph>
                  <Table
                    rowKey="id"
                    size="small"
                    loading={isLoading}
                    dataSource={data?.stale_deals ?? []}
                    pagination={false}
                    locale={{ emptyText: 'No stale deals' }}
                    columns={[
                      {
                        title: 'Opportunity',
                        key: 'opp',
                        render: (_, r) => (
                          <Link to={`/growth/opportunities/${r.id}`}>
                            {r.opportunity_number} – {r.opportunity_name}
                          </Link>
                        ),
                      },
                      { title: 'Customer', dataIndex: 'customer_name' },
                      { title: 'Owner', dataIndex: 'owner_name' },
                      { title: 'Last activity', dataIndex: 'last_activity_at', render: (v: string | null) => (v ? formatDate(v) : '—') },
                      { title: 'Days idle', dataIndex: 'days_idle', align: 'right', render: (v: number) => <Text type="danger">{v}</Text> },
                      { title: 'Value', dataIndex: 'value', align: 'right', render: (v: number) => formatCurrency(v) },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default WeeklySalesPerformancePage;
